import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
  getAllergies,
  deleteAllergy,
  Allergy,
  AllergyCreateUpdatePayload,
} from "../../api/allergy";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import AllergyModal from "../../components/AllergyModal";

const AllergyList: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const fetchedRef = useRef(false);
  const [isAllergyModalOpen, setIsAllergyModalOpen] = useState(false);
  const [selectedAllergy, setSelectedAllergy] =
    useState<AllergyCreateUpdatePayload | null>(null);

  const fetchAllergies = async () => {
    setLoading(true);
    try {
      const res = await getAllergies();
      const data = res?.data || [];
      setAllergies(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to load allergies");
      setAllergies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchAllergies();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');
      
      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const requestDelete = (id: string) => {
    setSelectedId(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await deleteAllergy(selectedId);
      setShowConfirm(false);
      setShowSuccess(true);
      fetchAllergies();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete allergy");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const handleOpenAllergyModal = (allergy: Allergy | null = null) => {
    setSelectedAllergy(allergy);
    setIsAllergyModalOpen(true);
  };

  const handleCloseAllergyModal = () => {
    setSelectedAllergy(null);
    setIsAllergyModalOpen(false);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Allergy Management
                </h2>
                <button
                  className="px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                  onClick={() => handleOpenAllergyModal()}
                >
                  + Add Allergy
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Allergy Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Created At
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allergies.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={3}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      allergies.map((allergy) => (
                        <tr key={allergy.id} className="border-t">
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {allergy.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(allergy.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div 
                              className="relative" 
                              ref={(el) => {
                                if (openMenuId === allergy.id) {
                                  (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                }
                              }}
                            >
                              <button
                                ref={(el) => {
                                  if (el) {
                                    buttonRefs.current[allergy.id] = el;
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                                onClick={(e) => {
                                  if (openMenuId === allergy.id) {
                                    setOpenMenuId(null);
                                    setDropdownPosition(null);
                                  } else {
                                    const button = e.currentTarget;
                                    const rect = button.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + window.scrollY + 4,
                                      right: window.innerWidth - rect.right,
                                    });
                                    setOpenMenuId(allergy.id);
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="12" cy="19" r="2" />
                                </svg>
                              </button>
                              {openMenuId === allergy.id && dropdownPosition && createPortal(
                                <div 
                                  className="dropdown-menu-container fixed w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    right: `${dropdownPosition.right}px`,
                                  }}
                                >
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      handleOpenAllergyModal(allergy);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      requestDelete(allergy.id);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>,
                                document.body
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <ConfirmModal
                isOpen={showConfirm}
                title="Delete Allergy"
                message="Are you sure you want to delete this allergy?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={() => setShowConfirm(false)}
                loading={loading}
              />

              <SuccessModal
                isOpen={showSuccess}
                title="Allergy Deleted"
                message="The allergy has been deleted successfully."
                onClose={() => setShowSuccess(false)}
              />
              <AllergyModal
                isOpen={isAllergyModalOpen}
                onClose={handleCloseAllergyModal}
                onSuccess={fetchAllergies}
                allergy={selectedAllergy}
              />
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AllergyList;